import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AgentTypeSelector from './components/AgentTypeSelector';
import ConnectionManager from './components/ConnectionManager';
import ConfigurationPanel from './components/ConfigurationPanel';
import EnvironmentToggle from './components/EnvironmentToggle';
import LaunchProgress from './components/LaunchProgress';

const LaunchAgent = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [environment, setEnvironment] = useState('sandbox');
  const [showProgress, setShowProgress] = useState(false);
  
  const [connections, setConnections] = useState({
    github: false,
    make: false,
    codex: false
  });

  const [config, setConfig] = useState({
    dataSource: '',
    region: '',
    topic: '',
    startDate: '',
    endDate: '',
    outputFormat: [],
    includeSentiment: true,
    includeTrends: false,
    includeDemographics: true,
    enableRealTime: false,
    sampleSize: '10000',
    confidenceLevel: '95',
    updateFrequency: '24'
  });

  useEffect(() => {
    // Set default dates
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
    
    setConfig(prev => ({
      ...prev,
      startDate: thirtyDaysAgo?.toISOString()?.split('T')?.[0],
      endDate: today?.toISOString()?.split('T')?.[0]
    }));
  }, []);

  const steps = [
    { id: 1, title: 'Agent Type', icon: 'Bot', description: 'Select AI agent type' },
    { id: 2, title: 'Connections', icon: 'Link', description: 'Configure service connections' },
    { id: 3, title: 'Configuration', icon: 'Settings', description: 'Set analysis parameters' },
    { id: 4, title: 'Deploy', icon: 'Rocket', description: 'Launch your agent' }
  ];

  const handleConnect = (serviceId) => {
    setConnections(prev => ({
      ...prev,
      [serviceId]: true
    }));
  };

  const handleDisconnect = (serviceId) => {
    setConnections(prev => ({
      ...prev,
      [serviceId]: false
    }));
  };

  const canProceedToNext = () => {
    switch (currentStep) {
      case 1:
        return selectedAgent !== '';
      case 2:
        return Object.values(connections)?.some(connected => connected);
      case 3:
        return config?.dataSource && config?.region && config?.topic && config?.outputFormat?.length > 0;
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleLaunch = () => {
    setShowProgress(true);
  };

  const handleProgressClose = () => {
    setShowProgress(false);
  };

  const StepNavigation = () => (
    <div className="bg-card border border-border rounded-xl p-6 mb-8">
      <div className="flex items-center justify-between">
        {steps?.map((step, index) => (
          <React.Fragment key={step?.id}>
            <div className="flex flex-col items-center">
              <button
                onClick={() => setCurrentStep(step?.id)}
                disabled={step?.id > currentStep && !canProceedToNext()}
                className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all duration-300 ${
                  currentStep === step?.id
                    ? 'bg-primary text-primary-foreground shadow-lg'
                    : currentStep > step?.id
                      ? 'bg-success text-white' :'bg-muted text-muted-foreground hover:bg-muted-foreground/20'
                }`}
              >
                <Icon 
                  name={currentStep > step?.id ? 'Check' : step?.icon} 
                  size={20} 
                />
              </button>
              <div className="text-center">
                <p className={`text-sm font-medium ${
                  currentStep >= step?.id ? 'text-card-foreground' : 'text-muted-foreground'
                }`}>
                  {step?.title}
                </p>
                <p className="text-xs text-text-secondary hidden sm:block">
                  {step?.description}
                </p>
              </div>
            </div>
            
            {index < steps?.length - 1 && (
              <div className={`hidden sm:block flex-1 h-0.5 mx-4 transition-all duration-300 ${
                currentStep > step?.id ? 'bg-success' : 'bg-border'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const StepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <AgentTypeSelector
            selectedAgent={selectedAgent}
            onAgentChange={setSelectedAgent}
          />
        );
      case 2:
        return (
          <ConnectionManager
            connections={connections}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
          />
        );
      case 3:
        return (
          <ConfigurationPanel
            config={config}
            onConfigChange={setConfig}
          />
        );
      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-foreground mb-2">
                Ready to Deploy
              </h2>
              <p className="text-text-secondary">
                Review your configuration and launch your AI agent
              </p>
            </div>
            <EnvironmentToggle
              environment={environment}
              onEnvironmentChange={setEnvironment}
            />
            {/* Configuration Summary */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="text-lg font-semibold text-card-foreground mb-4">
                Deployment Summary
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Agent Type:</span>
                    <span className="font-medium text-card-foreground capitalize">
                      {selectedAgent?.replace('-', ' ')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Environment:</span>
                    <span className="font-medium text-card-foreground capitalize">
                      {environment}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Data Source:</span>
                    <span className="font-medium text-card-foreground">
                      {config?.dataSource || 'Not selected'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Region:</span>
                    <span className="font-medium text-card-foreground">
                      {config?.region || 'Not selected'}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Connected Services:</span>
                    <span className="font-medium text-card-foreground">
                      {Object.values(connections)?.filter(Boolean)?.length} of 3
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Analysis Period:</span>
                    <span className="font-medium text-card-foreground">
                      {config?.startDate && config?.endDate 
                        ? `${config?.startDate} to ${config?.endDate}`
                        : 'Not set'
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Sample Size:</span>
                    <span className="font-medium text-card-foreground">
                      {config?.sampleSize ? `${parseInt(config?.sampleSize)?.toLocaleString()} points` : 'Default'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Output Formats:</span>
                    <span className="font-medium text-card-foreground">
                      {config?.outputFormat?.length || 0} selected
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="container mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
                <Icon name="Rocket" size={24} color="white" strokeWidth={2} />
              </div>
              <h1 className="text-3xl font-bold text-foreground">
                Launch AI Agent
              </h1>
            </div>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Configure and deploy specialized AI agents for automated market research and data analysis
            </p>
          </div>

          {/* Step Navigation */}
          <StepNavigation />

          {/* Step Content */}
          <div className="max-w-6xl mx-auto">
            <StepContent />
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mt-12 max-w-6xl mx-auto">
            <Button
              variant="outline"
              onClick={() => {
                if (currentStep > 1) {
                  setCurrentStep(currentStep - 1);
                } else {
                  navigate('/home-landing');
                }
              }}
              iconName="ArrowLeft"
              iconPosition="left"
            >
              {currentStep > 1 ? 'Previous' : 'Back to Home'}
            </Button>

            <div className="flex space-x-3">
              {currentStep < 4 ? (
                <Button
                  variant="default"
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={!canProceedToNext()}
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  Next Step
                </Button>
              ) : (
                <Button
                  variant="default"
                  onClick={handleLaunch}
                  disabled={!canProceedToNext()}
                  iconName="Rocket"
                  iconPosition="left"
                  className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90"
                >
                  Launch Agent
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Launch Progress Modal */}
      <LaunchProgress
        isVisible={showProgress}
        onClose={handleProgressClose}
        agentType={selectedAgent}
        environment={environment}
      />
    </div>
  );
};

export default LaunchAgent;